import React from "react";

import { getBreakdown, getToolsBreakdown } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type {
  Breakdown,
  DateRange,
  InsightDimension,
  InsightFilters,
  ToolDimension,
  ToolUsage,
} from "../../lib/types";
import {
  Button,
  Card,
  EmptyState,
  Page,
  Select,
  Pill,
  RowButton,
  Tabs,
  Skeleton,
  Sparkline,
  Table,
  type IconName,
  type Layer,
} from "../../design";
import { useInsightFilters } from "./filters";
import { TimePresets, usePreset } from "./range";
import { FilterBar, FilterPicker } from "./SpendExplorer";
import { AGENT_COLS, BreakdownTable, MODEL_COLS, type Ctx } from "./Visibility";
import { filtersOf, humanize, pct, toolLabel, useLoad } from "./util";

// A tool or server earns a "needs attention" row once it fails this often
// over enough calls.
const ATTENTION_RATE = 0.1;
const ATTENTION_MIN_CALLS = 5;
const ATTENTION_TOP = 5;

type Tab = "agents" | "models" | "mcp" | "skills" | "tools";

const TAB_ICON: Record<Tab, IconName> = {
  agents: "activity",
  models: "models",
  mcp: "link",
  skills: "sparkle",
  tools: "edit",
};

function isTab(v: string | undefined): v is Tab {
  return TABS.some((t) => t.value === v);
}

const TABS: { value: Tab; label: string; info: string }[] = [
  {
    value: "agents",
    label: "Agents",
    info: "Agent is the harness (Claude Code, Codex…); version is the exact build that sent the request.",
  },
  {
    value: "models",
    label: "Models",
    info: "Spend at the providers' public API prices.",
  },
  {
    value: "mcp",
    label: "MCP servers",
    info: "Calls to the tools each MCP server serves.",
  },
  { value: "skills", label: "Skills", info: "Times each skill was loaded." },
  {
    value: "tools",
    label: "Tools",
    info: "Every tool call agents made. A failure is a result the agent marked as an error.",
  },
];

export const INVENTORY_LAYERS: Layer<Tab>[] = TABS.map((t) => ({
  value: t.value,
  label: t.label,
  icon: TAB_ICON[t.value],
}));

const TOOL_TAB: Partial<Record<Tab, ToolDimension>> = {
  mcp: "mcp_server",
  skills: "skill",
  tools: "tool",
};

type Summary = {
  agents: Breakdown;
  models: Breakdown;
  servers: ToolUsage[];
  skills: ToolUsage[];
  tools: ToolUsage[];
};

function useSummary(
  authHeaders: AuthHeaders,
  base: string,
  range: DateRange,
  filters: InsightFilters,
) {
  return useLoad<Summary>(
    () =>
      Promise.all([
        getBreakdown(authHeaders, base, range, "kind", filters),
        getBreakdown(authHeaders, base, range, "model", filters),
        ...(["mcp_server", "skill", "tool"] as const).map((g) =>
          getToolsBreakdown(authHeaders, base, range, g, filters),
        ),
      ]).then(([agents, models, ...rest]) => {
        const [servers, skills, tools] = rest.map(
          (d) => (d as { tools: ToolUsage[] }).tools,
        );
        return {
          agents: agents as Breakdown,
          models: models as Breakdown,
          servers,
          skills,
          tools,
        };
      }),
    [authHeaders, base, range.start, range.end, JSON.stringify(filters)],
  );
}

function isNew(t: ToolUsage): boolean {
  const d = t.daily_calls;
  return (
    d.length > 7 &&
    d.slice(0, -7).every((v) => v === 0) &&
    d.slice(-7).some((v) => v > 0)
  );
}

function needsAttention(rows: ToolUsage[]): ToolUsage[] {
  return rows
    .filter(
      (t) => t.calls >= ATTENTION_MIN_CALLS && t.error_rate >= ATTENTION_RATE,
    )
    .sort((a, b) => b.errors - a.errors);
}

function NeedsAttention({
  data,
  onTab,
}: {
  data: Summary;
  onTab: (t: Tab, focus?: string) => void;
}) {
  const [all, setAll] = React.useState(false);
  const failing = [
    ...needsAttention(data.servers).map((t) => ({ t, kind: "MCP server" })),
    ...needsAttention(data.tools).map((t) => ({ t, kind: "Tool" })),
  ];
  const shown = all ? failing : failing.slice(0, ATTENTION_TOP);
  return (
    <Card
      flush
      title={
        <>
          Needs attention
          {failing.length > 0 && <Pill tone="bad">{failing.length}</Pill>}
        </>
      }
      info="Tools and MCP servers failing on 10% or more of at least 5 calls. Retrying a failing tool burns turns; check paths, permissions or the server's health."
      actions={
        failing.length > ATTENTION_TOP && (
          <Button variant="link" size="sm" onClick={() => setAll((v) => !v)}>
            {all ? "Show fewer" : `View all ${failing.length}`}
          </Button>
        )
      }
    >
      {failing.length === 0 ? (
        <EmptyState
          compact
          icon="check"
          title="Nothing is failing often enough to flag"
        />
      ) : (
        <Table label="Needs attention" size="sm">
          <tbody>
            {shown.map(({ t, kind }) => (
              <RowButton
                key={`${kind}:${t.key}`}
                label={`Open ${toolLabel(t.key)}`}
                onOpen={() => onTab(kind === "Tool" ? "tools" : "mcp", t.key)}
              >
                <td title={t.key}>
                  <span className="strong">{toolLabel(t.key)}</span>
                  <span className="muted"> · {kind}</span>
                </td>
                <td className="num muted">
                  {humanize(t.errors)} of {humanize(t.calls)} calls failed
                </td>
                <td className="num ast-att-rate">
                  <Pill tone="bad" icon="alert">
                    {pct(t.error_rate, 1)}
                  </Pill>
                </td>
              </RowButton>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

const MODEL_KEEP = new Set(["Requests", "Cache hit"]);

function ToolTable({
  ctx,
  dim,
  showUsers,
  focus,
}: {
  ctx: Ctx;
  dim: ToolDimension;
  showUsers: boolean;
  focus?: string;
}) {
  const { authHeaders, base, range } = ctx;
  const flt = filtersOf(ctx.filters);
  const { data, error } = useLoad(
    () => getToolsBreakdown(authHeaders, base, range, dim, flt),
    [authHeaders, base, range.start, range.end, dim, JSON.stringify(flt)],
  );
  const name = { tool: "Tool", mcp_server: "MCP server", skill: "Skill" }[dim];
  return (
    <Card flush>
      {error ? (
        <EmptyState compact icon="alert" title="Couldn't load this table">
          {error}
        </EmptyState>
      ) : !data ? (
        <div className="ds-card-pad">
          <Skeleton lines={5} />
        </div>
      ) : data.tools.length === 0 ? (
        <EmptyState compact title="Nothing in this range" />
      ) : (
        <Table label={`${name}s`}>
          <thead>
            <tr>
              <th>{name}</th>
              <th className="num">Calls</th>
              <th>Trend</th>
              <th className="num">Failed</th>
              <th className="num">Fail rate</th>
              <th className="num">Requests</th>
              {showUsers && <th className="num">People</th>}
            </tr>
          </thead>
          <tbody>
            {data.tools.map((t) => {
              const label = toolLabel(t.key);
              return (
                <tr
                  key={t.key}
                  className={t.key === focus ? "is-selected" : undefined}
                  ref={
                    t.key === focus
                      ? (el) => el?.scrollIntoView({ block: "center" })
                      : undefined
                  }
                >
                  <td className="strong" title={t.key}>
                    <span className="ast-name">
                      {label}
                      {dim === "tool" && t.mcp_server && label === t.key && (
                        <Pill>{t.mcp_server}</Pill>
                      )}
                      {dim === "mcp_server" && isNew(t) && (
                        <Pill tone="info" icon="sparkle">
                          New this week
                        </Pill>
                      )}
                    </span>
                  </td>
                  <td className="num">{humanize(t.calls)}</td>
                  <td className="ast-trend">
                    <Sparkline
                      values={t.daily_calls}
                      label={`Daily calls to ${label}`}
                    />
                  </td>
                  <td className="num">{humanize(t.errors)}</td>
                  <td className="num">
                    {!t.calls ? (
                      "—"
                    ) : t.error_rate >= ATTENTION_RATE ? (
                      <Pill tone="bad" icon="alert">
                        {pct(t.error_rate, 1)}
                      </Pill>
                    ) : (
                      pct(t.error_rate, 1)
                    )}
                  </td>
                  <td className="num">{humanize(t.requests)}</td>
                  {showUsers && <td className="num">{humanize(t.users)}</td>}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Body({
  tab,
  ctx,
  showUsers,
  focus,
}: {
  tab: Tab;
  ctx: Ctx;
  showUsers: boolean;
  focus?: string;
}) {
  const [agentGroup, setAgentGroup] = React.useState<"kind" | "client">("kind");
  const toolDim = TOOL_TAB[tab];
  if (tab === "agents")
    return (
      <>
        <div className="ana-chips">
          <Select
            label="Group by"
            value={agentGroup}
            options={[
              { value: "kind", label: "Agent" },
              { value: "client", label: "Version" },
            ]}
            onChange={setAgentGroup}
          />
        </div>
        <BreakdownTable
          ctx={ctx}
          dim={agentGroup}
          title={null}
          cols={AGENT_COLS.filter((c) => c.label !== "Spend")}
        />
      </>
    );
  if (tab === "models")
    return (
      <div className="ast-models">
        <BreakdownTable
          ctx={ctx}
          dim="model"
          title={null}
          cols={[
            ...MODEL_COLS.filter((c) => MODEL_KEEP.has(c.label)),
            {
              label: "Tokens",
              info: "Input + output + cache read + cache write.",
              num: true,
              cell: (m) =>
                humanize(
                  m.input_tokens +
                    m.output_tokens +
                    m.cache_read +
                    m.cache_write,
                ),
            },
          ]}
        />
      </div>
    );
  return toolDim ? (
    <ToolTable ctx={ctx} dim={toolDim} showUsers={showUsers} focus={focus} />
  ) : null;
}

export function Assets({
  authHeaders,
  base,
  showUsers = true,
  range: outer,
  initialTab,
  onTab,
  dimensions = [],
}: {
  authHeaders: AuthHeaders;
  base: string;
  showUsers?: boolean;
  /** Embedded under another page's header: use its range, skip our frame. */
  range?: DateRange;
  initialTab?: string;
  onTab?: (t: string) => void;
  dimensions?: InsightDimension[];
}) {
  const first: Tab = outer ? "tools" : "agents";
  const [tabState, setTabState] = React.useState<Tab>(first);
  const [tabPart, focus] = (initialTab ?? "").split(".");
  const allowed = TABS.filter(
    (t) => !outer || (t.value !== "agents" && t.value !== "models"),
  );
  const tab =
    isTab(tabPart) && allowed.some((t) => t.value === tabPart)
      ? tabPart
      : onTab
        ? first
        : tabState;
  const setTab = (t: Tab, f?: string) => {
    setTabState(t);
    onTab?.(f ? `${t}.${f}` : t);
  };
  const [preset, setPreset, own] = usePreset();
  const range = outer ?? own;
  const url = useInsightFilters();
  const filters = dimensions.length ? url.filters : [];
  const ctx: Ctx = { authHeaders, base, range, filters, onDrill: url.onDrill };
  const summary = useSummary(authHeaders, base, range, filtersOf(filters));
  const body = <Body tab={tab} ctx={ctx} showUsers={showUsers} focus={focus} />;

  const d = summary.data;
  const counts: Partial<Record<Tab, number>> = d
    ? {
        agents: d.agents.groups.length,
        models: d.models.groups.length,
        mcp: d.servers.length,
        skills: d.skills.length,
        tools: d.tools.length,
      }
    : {};
  const tabs = (
    <Tabs
      label="Inventory types"
      value={tab}
      onChange={(t) => setTab(t)}
      options={allowed.map((t) => ({
        value: t.value,
        label: t.label,
        count: counts[t.value],
      }))}
    />
  );
  const content = (
    <>
      {dimensions.length > 0 && (
        <>
          <FilterBar
            filters={filters}
            onRemove={(key) =>
              url.setFilters((all) => all.filter((f) => f.key !== key))
            }
          />
          <div className="ana-chips">
            <FilterPicker
              authHeaders={authHeaders}
              base={base}
              range={range}
              dimensions={dimensions}
              filters={filters}
              onPick={url.onDrill}
            />
          </div>
        </>
      )}
      {body}
      {d &&
        (tab === "tools" || tab === "mcp") &&
        (needsAttention(d.servers).length > 0 ||
          needsAttention(d.tools).length > 0) && (
          <NeedsAttention data={d} onTab={setTab} />
        )}
    </>
  );
  if (outer)
    return (
      <>
        {tabs}
        {content}
      </>
    );
  return (
    <Page
      title="Inventory"
      layers={{
        title: "Inventory types",
        value: tab,
        onChange: (next) => setTab(next),
        options: allowed.map((t) => ({
          value: t.value,
          label: t.label,
          icon: TAB_ICON[t.value],
          count: counts[t.value],
        })),
      }}
      actions={<TimePresets value={preset} onChange={setPreset} />}
    >
      {content}
    </Page>
  );
}
